import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, BellRing } from "lucide-react";
import { toast } from "sonner";
import { USERS } from "@/constants/testIds";
import UserNotificationsDialog from "@/components/UserNotificationsDialog";

const ROLE_LABEL = {
  admin: "Administrador",
  editor: "Editor",
  viewer: "Consulta",
  auditor: "Auditor",
};

const emptyForm = () => ({ email: "", name: "", role: "viewer", password: "" });

export default function Users() {
  const [items, setItems] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [notifDlgUser, setNotifDlgUser] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/users");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDlgOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, role: u.role, password: "" });
    setDlgOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", form);
        toast.success("Usuario creado");
      }
      setDlgOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Usuarios</h1>
          <div className="text-sm text-muted-foreground mt-1">{items.length} cuentas · sólo administradores gestionan.</div>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid={USERS.addBtn}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo usuario
        </Button>
      </div>

      <div className="rounded-xl border border-border/60 overflow-hidden" data-testid={USERS.list}>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Nombre</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Rol</th>
              <th className="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-border/50 hover:bg-secondary/40">
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="text-[11px] uppercase tracking-widest border border-border/60 rounded-full px-2 py-0.5">
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setNotifDlgUser(u)} data-testid={`user-notifications-${u.id}`} title="Notificaciones">
                    <BellRing className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => del(u)} data-testid={`user-delete-${u.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid={USERS.dialog}>
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>
              Los roles determinan permisos: Admin (todo), Editor (registros), Consulta (lectura), Auditor (log + lectura).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Consulta</SelectItem>
                  <SelectItem value="auditor">Auditor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label>
              <Input type="password" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <UserNotificationsDialog
        open={!!notifDlgUser}
        onOpenChange={(v) => !v && setNotifDlgUser(null)}
        user={notifDlgUser}
        onSaved={load}
      />
    </div>
  );
}
